import { forwardRef } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { productPromoLinks } from '@/constants/product-promo'
import { useProduct } from '@/context/product'
import { AccountingIcon } from './product-icons'
import { ProductPromoCard } from './product-promo-card'
import { CompanyContextRow, CreditContextRow } from './product-rows'

const Transition = forwardRef(
  (props: TransitionProps & { children: React.ReactElement }, ref) => (
    <Slide direction="up" ref={ref} {...props} />
  ),
)

export function ProductSheet() {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))
  const {
    product,
    companies,
    personName,
    personTaxId,
    firstName,
    hasAccounting,
    hasCredit,
    accountingAvailable,
    creditHasPending,
    selectedCompanyKey,
    sheetOpen,
    welcome,
    closeSheet,
    selectCredit,
    selectCompany,
  } = useProduct()

  const creditBlock = (
    <CreditContextRow
      current={!welcome && product === 'credit'}
      pending={product !== 'credit' && creditHasPending}
      name={personName}
      taxId={personTaxId}
      hasApplication={hasCredit}
      onClick={selectCredit}
    />
  )

  const accountingBlock = hasAccounting ? (
    <Stack>
      {companies.map((company) => (
        <CompanyContextRow
          key={company.deal_key}
          company={company}
          current={
            !welcome &&
            product === 'accounting' &&
            company.company_key === selectedCompanyKey
          }
          onClick={() => selectCompany(company)}
        />
      ))}
    </Stack>
  ) : accountingAvailable ? (
    <ProductPromoCard
      icon={<AccountingIcon />}
      title="Contabilidade para a sua PJ médica"
      description="Pague menos impostos nos plantões e emita notas pelo app."
      primaryLabel="Falar com especialista"
      primaryHref={productPromoLinks.accountingSpecialist}
    />
  ) : null

  const blocks = welcome
    ? [
        hasAccounting ? accountingBlock : null,
        hasCredit ? creditBlock : null,
        !hasAccounting ? accountingBlock : null,
        !hasCredit ? creditBlock : null,
      ]
    : product === 'credit'
      ? [creditBlock, accountingBlock]
      : [accountingBlock, creditBlock]

  return (
    <Dialog
      open={sheetOpen}
      onClose={closeSheet}
      fullScreen={isMobile}
      fullWidth
      maxWidth="xs"
      TransitionComponent={isMobile ? Transition : undefined}
      aria-label={welcome ? 'Por onde quer começar?' : 'Seus produtos Alume'}
      sx={{
        '& .MuiDialog-container': {
          alignItems: isMobile ? 'flex-end' : 'center',
        },
      }}
      PaperProps={{
        sx: isMobile
          ? {
              height: 'auto',
              maxHeight: '85%',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            }
          : undefined,
      }}
    >
      {isMobile && (
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: 'action.disabled',
            mx: 'auto',
            mt: 1,
          }}
        />
      )}
      <DialogTitle>
        {welcome
          ? `Olá, ${firstName || 'bem-vindo'}! Por onde quer começar?`
          : 'Seus produtos Alume'}
      </DialogTitle>
      {welcome && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 3, mt: -1 }}
        >
          Você pode trocar a qualquer momento pelo botão no topo.
        </Typography>
      )}
      <DialogContent>
        <Stack spacing={1.5}>{blocks}</Stack>
      </DialogContent>
    </Dialog>
  )
}
